/**
 * Client hooks over the conversation routes (docs/CONVERSATIONS.md, docs/API.md "Conversations", lane C1) for the
 * `/desk` UI (lane C5). One cache entry per thread (`convKeys.feed`) holds its messages by seq, the approvals seen
 * and the user's optimistic posts; the thread's SSE stream (`GET /threads/:id/stream`, resumed by `Last-Event-ID`
 * and `?after=`) merges into it. Everything is under the session root `me`, so sign-out drops it.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  THREAD_MESSAGES_PER_MINUTE,
  ThreadStreamEvent,
  type Approval,
  type ApprovalState,
  type Message,
  type Thread,
} from '@algoboo/schema/api'
import { buildUrl, request } from './client'
import { ApiError, errorCopy, isApiError } from './errors'
import { createSseParser, type SseFrame, type StreamStatus } from './stream'
import { attempt } from '../attempt'

export const convKeys = {
  threads: (state: 'open' | 'archived' | 'all') => ['me', 'threads', 'list', state] as const,
  thread: (id: string) => ['me', 'threads', 'detail', id] as const,
  feed: (id: string) => ['me', 'threads', 'feed', id] as const,
  memory: () => ['me', 'memory'] as const,
  agentPermissions: (agentId: string) => ['me', 'permissions', 'agent', agentId] as const,
}

// ------------------------------------------------------------------------------------------ the thread feed

/** A user message on its way: shown at once, replaced by the stored message when the API answers. */
export interface PendingMessage {
  localId: string
  /** The Idempotency-Key: a retry reuses it, so a lost answer never writes twice. */
  key: string
  text: string
  confirmMemory?: string
  declineMemory?: string
  state: 'sending' | 'failed'
  error?: string
  createdAt: string
}

export interface ThreadFeed {
  messages: Message[]
  lastSeq: number
  /** Newest known version of every approval (from proposals and `approval` events). */
  approvals: Record<string, Approval>
  pending: PendingMessage[]
}

export const EMPTY_FEED: ThreadFeed = { messages: [], lastSeq: 0, approvals: {}, pending: [] }

function newerApproval(a: Approval | undefined, b: Approval): boolean {
  return !a || Date.parse(b.updatedAt) >= Date.parse(a.updatedAt)
}

/** Merge messages (any order, duplicates allowed) into the feed: sorted by seq, one per seq. */
export function mergeMessages(feed: ThreadFeed, incoming: readonly Message[]): ThreadFeed {
  if (incoming.length === 0) return feed
  const bySeq = new Map(feed.messages.map((m) => [m.seq, m]))
  const approvals = { ...feed.approvals }
  let changed = false
  let pending = feed.pending
  for (const m of incoming) {
    const prev = bySeq.get(m.seq)
    // the stream can deliver the user's own message before the POST answers: it replaces the optimistic copy
    if (!prev && m.role === 'user' && m.kind === 'text') {
      const i = pending.findIndex((p) => p.state === 'sending' && p.text === m.body['text'])
      if (i >= 0) pending = pending.filter((_, j) => j !== i)
    }
    if (!prev || (m.approval && (!prev.approval || newerApproval(prev.approval, m.approval)))) {
      bySeq.set(m.seq, m)
      changed = true
    }
    if (m.approval && newerApproval(approvals[m.approval.id], m.approval)) approvals[m.approval.id] = m.approval
  }
  if (!changed) return { ...feed, approvals }
  const messages = [...bySeq.values()].sort((a, b) => a.seq - b.seq)
  const ids = new Set(messages.map((m) => m.id))
  return { ...feed, messages, approvals, lastSeq: Math.max(feed.lastSeq, messages[messages.length - 1]?.seq ?? 0), pending: pending.filter((p) => !ids.has(p.localId)) }
}

export function mergeApproval(feed: ThreadFeed, a: Approval): ThreadFeed {
  if (!newerApproval(feed.approvals[a.id], a)) return feed
  return { ...feed, approvals: { ...feed.approvals, [a.id]: a } }
}

const RANK: Record<ApprovalState, number> = { pending: 0, approved: 1, rejected: 2, expired: 2, executed: 3, failed: 3 }

export interface ApprovalView {
  id: string
  state: ApprovalState
  expiresAt: string | null
  source: string | null
  result: Record<string, unknown> | null
  approval: Approval | null
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)
const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null)

/**
 * The effective state of an approval: the newest `Approval` row seen, advanced by the decision and outcome messages
 * that follow it in the thread (rule 3 of docs/CONVERSATIONS.md), so a missed `approval` event never leaves a stale
 * button.
 */
export function approvalView(feed: ThreadFeed, id: string): ApprovalView {
  const a = feed.approvals[id] ?? null
  let v: ApprovalView = { id, state: a?.state ?? 'pending', expiresAt: a?.expiresAt ?? null, source: a?.source ?? null, result: (a?.result as Record<string, unknown> | null) ?? null, approval: a }
  for (const m of feed.messages) {
    if (m.body['approvalId'] !== id) continue
    let next: ApprovalState | null = null
    if (m.kind === 'approval') next = m.body['decision'] === 'rejected' ? 'rejected' : 'approved'
    else if (m.kind === 'status') {
      const s = str(m.body['state'])
      if (s && s in RANK) next = s as ApprovalState
    }
    if (next && RANK[next] > RANK[v.state]) {
      v = { ...v, state: next, source: str(m.body['source']) ?? v.source, result: obj(m.body['result']) ?? v.result }
    }
  }
  return v
}

// ------------------------------------------------------------------------------------------ reads

export const threadsQuery = (state: 'open' | 'archived' | 'all') =>
  queryOptions({
    queryKey: convKeys.threads(state),
    queryFn: ({ signal }) => request('listThreads', { signal, query: { state, limit: 100 } }),
    refetchInterval: 60_000,
  })

export function useThreads(state: 'open' | 'archived' | 'all', enabled = true) {
  return useQuery({ ...threadsQuery(state), enabled })
}

export const threadDetailQuery = (id: string) => queryOptions({ queryKey: convKeys.thread(id), queryFn: ({ signal }) => request('getThread', { signal, params: { id } }) })

export function useThreadDetail(id: string | null) {
  return useQuery({ ...threadDetailQuery(id ?? ''), enabled: !!id })
}

/** Messages cap of the first load; older ones stay on the server (the desk is for what is current). */
export const HISTORY_CAP = 1000

/** Every message after `after`, page by page. */
export async function loadMessages(threadId: string, after: number, signal?: AbortSignal): Promise<{ items: Message[]; lastSeq: number }> {
  const items: Message[] = []
  let cursor = after
  let lastSeq = after
  for (let i = 0; i < 50; i++) {
    const page = await request('listThreadMessages', { params: { id: threadId }, query: { after: cursor, limit: 200 }, ...(signal ? { signal } : {}) })
    items.push(...page.items)
    lastSeq = Math.max(lastSeq, page.lastSeq)
    if (page.nextAfter === null || page.items.length === 0) break
    cursor = page.nextAfter
  }
  return { items, lastSeq }
}

/** Where the first load starts: the newest HISTORY_CAP messages, and always the deep-linked one. */
export function historyStart(lastSeq: number, focusSeq: number | null): number {
  const start = Math.max(0, lastSeq - HISTORY_CAP)
  return focusSeq !== null && focusSeq > 0 ? Math.max(0, Math.min(start, focusSeq - 20)) : start
}

/** The first load of a thread's messages (merged into what the stream already delivered). */
export const threadFeedQuery = (qc: QueryClient, thread: Pick<Thread, 'id' | 'lastSeq'>, focusSeq: number | null = null) =>
  queryOptions({
    queryKey: convKeys.feed(thread.id),
    queryFn: async ({ signal }): Promise<ThreadFeed> => {
      const r = await loadMessages(thread.id, historyStart(thread.lastSeq, focusSeq), signal)
      const prev = qc.getQueryData<ThreadFeed>(convKeys.feed(thread.id)) ?? EMPTY_FEED
      return mergeMessages({ ...prev, lastSeq: Math.max(prev.lastSeq, r.lastSeq) }, r.items)
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

export function useThreadFeed(thread: Pick<Thread, 'id' | 'lastSeq'> | null, focusSeq: number | null = null) {
  const qc = useQueryClient()
  return useQuery({ ...threadFeedQuery(qc, thread ?? { id: '', lastSeq: 0 }, focusSeq), enabled: !!thread })
}

// ------------------------------------------------------------------------------------------ the stream

export interface ThreadStreamOptions {
  threadId: string
  /** The highest seq the client holds (read at every (re)connect). */
  after: () => number
  onEvent: (e: ThreadStreamEvent) => void
  onStatus: (s: StreamStatus) => void
  /** After a reconnect: reload what the stream does not replay (approval rows, thread state). */
  onResync?: () => void
  fetch?: typeof fetch
  /** Delay before reconnect attempt n (1-based). */
  delay?: (attempt: number) => number
}

/**
 * Open the thread's SSE stream over fetch (so `Last-Event-ID` is sent on every reconnect) and keep it open: a closed
 * or failed connection reconnects with backoff from the highest seq delivered. Returns the close function.
 */
export function openThreadStream(o: ThreadStreamOptions): () => void {
  const controller = new AbortController()
  const fetchImpl = o.fetch ?? globalThis.fetch
  const delay = o.delay ?? ((n: number) => Math.min(30_000, 1_000 * 2 ** Math.min(n - 1, 5)))
  let attempt = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let lastId: string | null = null
  let shown: StreamStatus | null = null
  const status = (s: StreamStatus) => {
    if (s !== shown) o.onStatus((shown = s))
  }
  const onFrame = (f: SseFrame) => {
    if (f.id !== undefined && /^\d+$/.test(f.id)) lastId = f.id
    let json: unknown
    try {
      json = JSON.parse(f.data)
    } catch {
      return
    }
    const parsed = ThreadStreamEvent.safeParse(json)
    if (parsed.success) o.onEvent(parsed.data)
    else console.warn('[thread-stream] dropped an event that failed validation', f.event)
  }
  const connect = async () => {
    const resumed = attempt > 0
    status(resumed ? 'reconnecting' : 'connecting')
    try {
      const after = Math.max(o.after(), lastId ? Number(lastId) : 0)
      const headers: Record<string, string> = { Accept: 'text/event-stream' }
      if (after > 0) headers['Last-Event-ID'] = String(after)
      const res = await fetchImpl(buildUrl('streamThread', { params: { id: o.threadId }, query: { after } }), { headers, credentials: 'include', signal: controller.signal })
      if (!res.ok || !res.body) throw new Error(`thread stream http ${res.status}`)
      status('live')
      attempt = 0
      if (resumed) o.onResync?.()
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      const parse = createSseParser()
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        for (const f of parse(decoder.decode(value, { stream: true }))) onFrame(f)
      }
      throw new Error('thread stream closed')
    } catch {
      if (controller.signal.aborted) return
      attempt++
      status('reconnecting')
      timer = setTimeout(() => void connect(), delay(attempt))
    }
  }
  void connect()
  return () => {
    controller.abort()
    if (timer) clearTimeout(timer)
  }
}

function updateThreadInLists(qc: QueryClient, t: Thread) {
  for (const state of ['open', 'archived', 'all'] as const) {
    qc.setQueryData<{ items: Thread[]; nextCursor: string | null }>(convKeys.threads(state), (d) => (d ? { ...d, items: d.items.map((x) => (x.id === t.id ? t : x)) } : d))
  }
}

/** Keep the thread's feed live while mounted; `status` drives the "reconnecting" line. */
export function useThreadStream(threadId: string | null, enabled: boolean): StreamStatus {
  const qc = useQueryClient()
  const [status, setStatus] = useState<StreamStatus>('off')
  useEffect(() => {
    if (!threadId || !enabled) return
    const key = convKeys.feed(threadId)
    const close = openThreadStream({
      threadId,
      after: () => qc.getQueryData<ThreadFeed>(key)?.lastSeq ?? 0,
      onStatus: setStatus,
      onEvent: (e) => {
        if (e.type === 'message') qc.setQueryData<ThreadFeed>(key, (f) => mergeMessages(f ?? EMPTY_FEED, [e.message]))
        else if (e.type === 'approval') qc.setQueryData<ThreadFeed>(key, (f) => mergeApproval(f ?? EMPTY_FEED, e.approval))
        else {
          updateThreadInLists(qc, e.thread)
          qc.setQueryData(convKeys.thread(threadId), (d: object | undefined) => (d ? { ...d, ...e.thread, workState: e.workState } : d))
        }
      },
      onResync: () => {
        // the stream replays messages after our seq; approval rows and the thread's state are read again
        void request('getThread', { params: { id: threadId } })
          .then((d) => {
            qc.setQueryData(convKeys.thread(threadId), d)
            qc.setQueryData<ThreadFeed>(key, (f) => d.approvals.reduce(mergeApproval, f ?? EMPTY_FEED))
          })
          .catch(() => undefined)
        void qc.invalidateQueries({ queryKey: ['me', 'threads', 'list'] })
      },
    })
    return close
  }, [threadId, enabled, qc])
  return threadId && enabled ? status : 'off'
}

// ------------------------------------------------------------------------------------------ writes

function newKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** User-facing copy for a failed post (rate limit and archived thread in words, not codes). */
export function postErrorCopy(e: unknown): string {
  if (isApiError(e) && e.code === 'RATE_LIMITED') {
    const s = e.retryAfterMs !== undefined ? Math.max(1, Math.ceil(e.retryAfterMs / 1000)) : null
    return `You can send ${THREAD_MESSAGES_PER_MINUTE} messages a minute.${s ? ` Try again in ${s} s.` : ' Wait a moment, then send again.'}`
  }
  if (isApiError(e)) return errorCopy(e.code).body
  return 'Could not send. Check your connection and try again.'
}

const sentAt: number[] = []

/** Client-side guard for the 30/min limit: null when a message may go now, else the copy. */
export function localRateLimit(now = Date.now()): string | null {
  while (sentAt.length && now - sentAt[0]! > 60_000) sentAt.shift()
  if (sentAt.length < THREAD_MESSAGES_PER_MINUTE) return null
  const s = Math.max(1, Math.ceil((60_000 - (now - sentAt[0]!)) / 1000))
  return `You can send ${THREAD_MESSAGES_PER_MINUTE} messages a minute. Try again in ${s} s.`
}

/** Count one send against the local guard. */
export function recordSend(now = Date.now()): void {
  sentAt.push(now)
}

/** Test hook: forget the local send times. */
export function resetLocalRateLimit(): void {
  sentAt.length = 0
}

export interface SendExtra {
  confirmMemory?: string
  declineMemory?: string
}

/** Post the user's message: optimistic, idempotent (one key per action, reused on retry). */
export function usePostMessage(threadId: string | null) {
  const qc = useQueryClient()
  const run = useCallback(
    async (p: PendingMessage) => {
      if (!threadId) return
      const key = convKeys.feed(threadId)
      const set = (fn: (f: ThreadFeed) => ThreadFeed) => qc.setQueryData<ThreadFeed>(key, (f) => fn(f ?? EMPTY_FEED))
      set((f) => ({ ...f, pending: [...f.pending.filter((x) => x.localId !== p.localId), { ...p, state: 'sending' }] }))
      recordSend()
      await attempt(
        async () => {
          const body = { text: p.text, ...(p.confirmMemory ? { confirmMemory: p.confirmMemory } : {}), ...(p.declineMemory ? { declineMemory: p.declineMemory } : {}) }
          const m = await request('postThreadMessage', { params: { id: threadId }, body, idempotencyKey: p.key })
          set((f) => {
            const merged = mergeMessages(f, [m])
            return { ...merged, pending: merged.pending.filter((x) => x.localId !== p.localId) }
          })
          void qc.invalidateQueries({ queryKey: ['me', 'threads', 'list'] })
        },
        (e) => {
          set((f) => ({ ...f, pending: f.pending.map((x) => (x.localId === p.localId ? { ...x, state: 'failed', error: postErrorCopy(e) } : x)) }))
        },
      )
    },
    [qc, threadId],
  )
  const send = useCallback(
    (text: string, extra: SendExtra = {}): string | null => {
      const t = text.trim()
      if (!t || !threadId) return null
      const limited = localRateLimit()
      if (limited) return limited
      const id = newKey()
      void run({ localId: id, key: id, text: t, ...extra, state: 'sending', createdAt: new Date().toISOString() })
      return null
    },
    [run, threadId],
  )
  const retry = useCallback((p: PendingMessage) => void run(p), [run])
  const discard = useCallback(
    (localId: string) => {
      if (threadId) qc.setQueryData<ThreadFeed>(convKeys.feed(threadId), (f) => (f ? { ...f, pending: f.pending.filter((x) => x.localId !== localId) } : f))
    },
    [qc, threadId],
  )
  return { send, retry, discard }
}

/** Approve or reject: one idempotency key per click; the answer (or a 409) updates the thread. */
export function useDecideApproval(threadId: string) {
  const qc = useQueryClient()
  return useMutation<Approval, Error, { id: string; decision: 'approve' | 'reject' }>({
    mutationFn: ({ id, decision }) => request(decision === 'approve' ? 'approveApproval' : 'rejectApproval', { params: { id }, body: {}, idempotencyKey: newKey() }),
    onSuccess: (a) => {
      qc.setQueryData<ThreadFeed>(convKeys.feed(threadId), (f) => mergeApproval(f ?? EMPTY_FEED, a))
      void qc.invalidateQueries({ queryKey: ['me', 'threads'] , predicate: (q) => q.queryKey[2] !== 'feed' })
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.code === 'APPROVAL_EXPIRED' || e.code === 'APPROVAL_NOT_PENDING')) {
        void request('getThread', { params: { id: threadId } })
          .then((d) => qc.setQueryData<ThreadFeed>(convKeys.feed(threadId), (f) => d.approvals.reduce(mergeApproval, f ?? EMPTY_FEED)))
          .catch(() => undefined)
      }
    },
  })
}

// ------------------------------------------------------------------------------------------ memory

export function useMyMemory(enabled = true) {
  return useQuery({ queryKey: convKeys.memory(), queryFn: ({ signal }) => request('listMyMemory', { signal, query: {} }), enabled })
}

export function usePutMemory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { key: string; value: unknown }) => request('putMyMemory', { params: { key: v.key }, body: { value: v.value }, idempotencyKey: newKey() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: convKeys.memory() }),
  })
}

export function useDeleteMemory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (key: string) => request('deleteMyMemory', { params: { key }, idempotencyKey: newKey() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: convKeys.memory() }),
  })
}

// ------------------------------------------------------------------------------------------ unread (localStorage)

const SEEN_PREFIX = 'algoboo.desk.seen.'
const seenListeners = new Set<() => void>()
const seenCache = new Map<string, Record<string, number>>()

function readSeenRaw(userId: string): Record<string, number> {
  const hit = seenCache.get(userId)
  if (hit) return hit
  let v: Record<string, number> = {}
  try {
    const raw = globalThis.localStorage?.getItem(SEEN_PREFIX + userId)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    if (parsed && typeof parsed === 'object') v = Object.fromEntries(Object.entries(parsed).filter(([, n]) => typeof n === 'number' && Number.isFinite(n))) as Record<string, number>
  } catch {
    // storage blocked or corrupt: nothing seen
  }
  seenCache.set(userId, v)
  return v
}

/** Mark the thread read up to `seq` (never backwards). */
export function markSeen(userId: string, threadId: string, seq: number): void {
  const cur = readSeenRaw(userId)
  if ((cur[threadId] ?? 0) >= seq) return
  const next = { ...cur, [threadId]: seq }
  seenCache.set(userId, next)
  try {
    globalThis.localStorage?.setItem(SEEN_PREFIX + userId, JSON.stringify(next))
  } catch {
    // storage blocked: read state lasts for this page
  }
  for (const fn of seenListeners) fn()
}

/** Test hook. */
export function resetSeen(): void {
  seenCache.clear()
}

export function useSeen(userId: string | null): Record<string, number> {
  const subscribe = useCallback((fn: () => void) => {
    seenListeners.add(fn)
    return () => void seenListeners.delete(fn)
  }, [])
  const get = useCallback(() => (userId ? readSeenRaw(userId) : EMPTY_SEEN), [userId])
  return useSyncExternalStore(subscribe, get, get)
}
const EMPTY_SEEN: Record<string, number> = {}

export function unreadCount(t: Pick<Thread, 'id' | 'lastSeq'>, seen: Record<string, number>): number {
  return Math.max(0, t.lastSeq - (seen[t.id] ?? 0))
}

/** The render clock (moved to lib/now.ts, lane BOO1). */
export { useNow } from '../now'
